import { css } from '@emotion/react';
import { DialogContent, FormControl, FormLabel, Grid, Typography } from '@material-ui/core';
import GoogleAddress from 'components/shared/GoogleMap/Address';
import { useColors } from 'hooks/useColors';
import { userApi } from 'lib/api/userApi';
import { auth } from 'lib/auth';
import { formatAddressComponent, required } from 'lib/form';
import { i18n } from 'lib/i18n';
import { useRouter } from 'next/router';
import { Dispatch, FC, SetStateAction } from 'react';
import { Field as FinalField, Form } from 'react-final-form';
import { useRecoilState } from 'recoil';
import { UserUpdateSettings } from 'src/types/userSettings';
import { userStore } from 'stores/userStore';
import { Button } from 'bondsports-utils';
import dayjs from 'dayjs';
import { EDateFormats } from 'types/times';
import { IColors } from '@bondsports/utils';
import { GenderAndBirthDate } from './GenderAndBirthDate';

const dialogTitleCss = (colors: IColors) => css`
	text-align: center;
	width: 100%;
	color: ${colors.tx_text_primary};

	&.MuiTypography-h5 {
		font-weight: 500;
		font-size: 2.2rem;
	}
`;

const userFormControlCss = css`
	.MuiFormLabel-root {
		padding: 8px 0;
	}

	&.MuiFormControl-root {
		padding: 10px 0;
	}
	width: 100%;
`;

const errorColorCss = (colors: IColors) => css`
	color: ${colors.tx_error};
	font-size: 1.4rem;
	text-align: center;
`;

const signUpFormCss = css`
	&.MuiTabs-root {
		display: none;
	}

	div#skipStepTwo {
		display: flex;
	}
`;

const signUpDialogContentCss = css`
	&.MuiDialogContent-root {
		padding: 8px 20px;
		overflow: visible;
	}
`;

const submitBtnCss = css`
	width: 100%;
`;

const dialogNotesCss = (colors: IColors) => css`
	text-align: center;
	width: 100%;
	color: ${colors.tx_text_primary};
	font-size: 14px;
	font-weight: 500;
`;

const familyRegistrationNote = css`
	margin-top: 12px;
	margin-bottom: 12px;
`;

interface SignUpUser {
	activity?: number[];
	address?: {
		address_components?: google.maps.GeocoderAddressComponent[];
	};
	birthDate: string;
	gender?: number;
}

interface SignUpUserFormProps {
	postFromSignUp?: (success: boolean) => void;
	setIsNextStep: Dispatch<SetStateAction<boolean>>;
	setIsUserSignUp: Dispatch<SetStateAction<boolean>>;
	onClose: // eslint-disable-next-line @typescript-eslint/ban-types
	((event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void) | undefined;
}

const SignUpUserForm: FC<SignUpUserFormProps> = props => {
	const { onClose, postFromSignUp, setIsNextStep, setIsUserSignUp } = props;

	const { colors } = useColors();

	const [, setUserSettings] = useRecoilState(userStore.userSettings);
	const [, setLoginSettings] = useRecoilState(userStore.loginSettings);
	// create redirection to user profile
	const router = useRouter();

	const onSignUp = (values: SignUpUser) => {
		/**
		 * MUST to fix address parsing or find new lib for this !!!
		 */

		const formattedAddressData = formatAddressComponent(
			values?.address?.address_components as google.maps.GeocoderAddressComponent[]
		);

		// set sports
		const sports: { sports: number; levelOfPlay: number }[] = [];

		if (values?.activity) {
			values?.activity.map((data: number) => {
				return sports.push({ sports: data, levelOfPlay: 1 });
			});
		}

		// set data to send
		const data: UserUpdateSettings = {
			address: formattedAddressData,
			profile: {
				birthDate: dayjs(values.birthDate).format(EDateFormats.YYYY_MM_DD),
				gender: values.gender,
			},
			athlete: {
				sports: sports,
			},
		};

		userApi
			.postUpdateUserSettings(data)
			.then(async () => {
				// update user settings
				const userSettingsData = await userApi.getUserSettings_DEPRICATED();
				setUserSettings({
					...userSettingsData.data,
					id: auth.getUserId(),
				});

				setLoginSettings({
					isLogin: true,
					userToken: auth.getToken(),
					userId: auth.getUserId(),
					isCompleteLoginProcess: true,
				});
				setIsNextStep(false);
				setIsUserSignUp(false);
				if (postFromSignUp) {
					// requested to handle it outside this scope
					postFromSignUp(true);
				} else {
					onClose && onClose({}, 'escapeKeyDown');
					router.push(`/user/profile`);
				}
			})
			.catch(() => {
				setIsNextStep(false);
				setIsUserSignUp(false);
				if (postFromSignUp) {
					// requested to handle it outside this scope
					postFromSignUp(true);
				} else {
					onClose && onClose({}, 'escapeKeyDown');
					router.push(`/user/profile`);
				}
			});
	};

	return (
		<Form
			data-aid="SignUpUserForm"
			onSubmit={onSignUp}
			initialValues={{
				birthDate: dayjs().toDate(),
			}}
			render={({ handleSubmit, submitting, invalid }) => {
				return (
					<form data-aid="SignUpUserForm-form" autoComplete="off" onSubmit={handleSubmit}>
						<Grid container spacing={2} css={signUpFormCss}>
							<Grid item xs={12}>
								<Typography component="h1" variant="h5" css={dialogTitleCss(colors)} id="login-dialog-title">
									{i18n.loginDialog.personal}
								</Typography>
								<div css={familyRegistrationNote}>
									<Typography css={dialogNotesCss(colors)} style={{ fontSize: '14px' }}>
										{i18n.loginDialog.familySignupNotes.accountDetails}
									</Typography>
								</div>
								<DialogContent css={signUpDialogContentCss}>
									<GenderAndBirthDate />
									{/* Address */}
									<FinalField name="address" validate={required} displayEmpty>
										{({ input, meta }) => {
											return (
												<Grid container justify="center">
													<FormControl fullWidth css={userFormControlCss}>
														<FormLabel htmlFor="userAddress">
															{i18n.user.profile.address}
															{meta.error && meta.touched && <span css={errorColorCss(colors)}> {meta.error}</span>}
														</FormLabel>
														<GoogleAddress {...input} />
													</FormControl>
												</Grid>
											);
										}}
									</FinalField>

									<Grid container justify="center">
										{/* <DialogActions> */}
										<Button
											data-aid="button-SignUpUserForm-save"
											theme="primary"
											sizer="M"
											disabled={submitting || invalid}
											type="submit"
											css={submitBtnCss}
										>
											{i18n.buttons.save}
										</Button>
									</Grid>
								</DialogContent>
							</Grid>
						</Grid>
					</form>
				);
			}}
		/>
	);
};

export default SignUpUserForm;
